const STORAGE_KEY = "refabric_first_touch";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 256;
const MAX_TOTAL_BYTES = 2048;

const ALLOWLIST = ["gclid", "gad_source", "gbraid", "wbraid", "fbclid", "_gl", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

type Stored = Record<string, string> & { expires_at?: string };

const safeGet = (): Stored | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed || typeof parsed !== "object") return null;

    return parsed;
  } catch {
    return null;
  }
};

const safeSet = (value: Stored): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore: quota exceeded, private mode, disabled storage — caller falls back to current-URL behavior
  }
};

const isExpired = (entry: Stored | null): boolean => {
  if (!entry || !entry.expires_at) return true;
  const expiry = Date.parse(entry.expires_at);
  if (Number.isNaN(expiry)) return true;

  return Date.now() >= expiry;
};

const truncate = (value: string): string => (value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value);

const enforceTotalCap = (entry: Stored): Stored => {
  const keys = Object.keys(entry).filter((k) => k !== "expires_at");
  // Drop keys from the end (lower-priority later keys in ALLOWLIST drop first) until under cap.
  while (JSON.stringify(entry).length > MAX_TOTAL_BYTES && keys.length > 0) {
    const dropKey = keys.pop() as string;
    delete entry[dropKey];
  }

  return entry;
};

export const parseCurrentSearch = (): Record<string, string> => {
  const result: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      result[key] = value;
    });
  } catch {
    // Ignore
  }

  return result;
};

const collectAllowlisted = (source: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {};
  ALLOWLIST.forEach((key) => {
    const value = source[key];
    if (typeof value === "string" && value.length > 0) {
      out[key] = truncate(value);
    }
  });

  return out;
};

export const captureFirstTouchOnce = (): void => {
  try {
    const existing = safeGet();
    if (existing && !isExpired(existing)) {
      return;
    }

    const current = parseCurrentSearch();
    const allowlisted = collectAllowlisted(current);
    const referrer = typeof document !== "undefined" ? document.referrer || "" : "";

    if (Object.keys(allowlisted).length === 0 && !referrer) {
      return;
    }

    const entry: Stored = {
      ...allowlisted,
      expires_at: new Date(Date.now() + TTL_MS).toISOString(),
    };

    if (referrer) {
      entry.referrer = truncate(referrer);
    }

    safeSet(enforceTotalCap(entry));
  } catch {
    // Never throw out of the capture path.
  }
};

export const readFirstTouch = (): Record<string, string> => {
  const entry = safeGet();
  if (!entry || isExpired(entry)) return {};
  const { expires_at: _expires, ...rest } = entry;

  return rest;
};

export const getAttributionParams = (): Record<string, string> => {
  captureFirstTouchOnce();

  return { ...readFirstTouch(), ...collectAllowlisted(parseCurrentSearch()) };
};
